import type { Command } from 'common/Prose/Commands'
import {
  isVoteListSorted,
  sortTree,
  toTree,
  flattenTree,
  getVoteListItems,
} from 'common/Prose/utils/lists/sortListUtils'
import { schema, StrongNode, nodes } from 'prose-core'
import { ResolvedPos } from 'prosemirror-model'
import { SORT_ORDER } from '../constants'

/**
 * Creates a `Command` to sort a vote list in ascending or descending order.
 * You may omit `$pos` if you can guarantee that `selection.$from` is inside `doc->vote_list->vote_list_item->paragraph`,
 * otherwise the command will simply not run.
 * Alternatively, you can pass in a resolved position pointing to the `vote_list`
 *
 * Note: This is a factory that returns a `Command`
 * @param sortOrder either Ascending or Descending order
 * @param $pos the resolved position before any `vote_list_item` node (not the pos of `vote_list`). Will use `selection.$from` if not provided
 */
export const sortVoteList =
  (sortOrder: SORT_ORDER, $pos?: ResolvedPos): Command =>
  (state, dispatch) => {
    const { selection, tr } = state
    if (!$pos) {
      $pos = selection.$from
    }
    const voteListItemNode = $pos.node(-1)
    const voteListNode = $pos.node(-2) as StrongNode<typeof nodes.vote_list>
    if (!(voteListNode?.type === schema.nodes.vote_list && voteListItemNode?.type === schema.nodes.vote_list_item))
      return false
    const voteListPos = $pos.before(-2)
    const voteListItems = getVoteListItems(voteListNode)
    const tree = toTree(voteListItems)
    if (isVoteListSorted(tree, sortOrder)) return false // do nothing if the list is already sorted
    const sortedTree = sortTree(tree, sortOrder)
    const sortedVoteListItems = flattenTree(sortedTree)

    tr.replaceWith(voteListPos, voteListPos + voteListNode.nodeSize, sortedVoteListItems)
    dispatch?.(tr)
    return true
  }
